import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faXmark } from '@fortawesome/pro-regular-svg-icons';
// @ts-ignore
import style from './Composer.module.scss?module';

const ACCEPTED = 'image/jpeg,image/png,image/webp';
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp']);

interface ComposerProps {
  onSubmit: (body: string, images: File[]) => Promise<boolean>;
  isSending: boolean;
  sendError: string | null;
}

export default function Composer({ onSubmit, isSending, sendError }: ComposerProps) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const trimmedBody = body.trim();
  const canSend = !isSending && (trimmedBody.length > 0 || files.length > 0);

  // Track preview URLs keyed by File identity so we don't revoke and re-create
  // the URL of an unchanged file on every render. The map is reconciled inside
  // a useEffect — mutating it during render would violate React's "render is
  // pure" rule and would produce duplicate URLs under StrictMode/Concurrent.
  const previewMapRef = useRef<Map<File, string>>(new Map());
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  useEffect(() => {
    const map = previewMapRef.current;
    // Add URLs for newly-added files.
    for (const file of files) {
      if (!map.has(file)) {
        map.set(file, URL.createObjectURL(file));
      }
    }
    // Revoke and remove URLs for files that have been dropped.
    const present = new Set(files);
    for (const [file, url] of Array.from(map.entries())) {
      if (!present.has(file)) {
        URL.revokeObjectURL(url);
        map.delete(file);
      }
    }
    setPreviewUrls(files.map((f) => map.get(f) ?? ''));
  }, [files]);

  // Final cleanup on unmount.
  useEffect(() => {
    return () => {
      const m = previewMapRef.current;
      for (const url of m.values()) {
        URL.revokeObjectURL(url);
      }
      m.clear();
    };
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalError(null);
    const selected = Array.from(e.target.files ?? []);
    if (selected.length === 0) return;

    const next: File[] = [...files];
    for (const file of selected) {
      if (!ACCEPTED_MIMES.has(file.type)) {
        setLocalError('File type not allowed (only JPG, PNG, WebP).');
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        setLocalError('File too large (max. 10 MB per image).');
        continue;
      }
      if (next.length >= MAX_FILES) {
        setLocalError(`Maximum ${MAX_FILES} images per message.`);
        break;
      }
      next.push(file);
    }
    setFiles(next);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    const ok = await onSubmit(trimmedBody, files);
    if (ok) {
      setBody('');
      setFiles([]);
      setLocalError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  return (
    <form className={style.Composer} onSubmit={handleSubmit}>
      {files.length > 0 && (
        <div className={style.Thumbs}>
          {files.map((file, i) => (
            <div
              key={`${file.name}|${file.size}|${file.lastModified}`}
              className={style.Thumb}
            >
              <img src={previewUrls[i]} alt="" />
              <button
                type="button"
                className={style.RemoveBtn}
                onClick={() => removeFile(i)}
                aria-label="Remove image"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className={style.InputRow}>
        <button
          type="button"
          className={style.IconBtn}
          onClick={() => fileInputRef.current?.click()}
          title="Attach images"
          aria-label="Attach images"
          disabled={isSending || files.length >= MAX_FILES}
        >
          <FontAwesomeIcon icon={faImage} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          multiple
          hidden
          onChange={handleFileSelect}
        />
        <textarea
          className={style.Textarea}
          placeholder="Write to us…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          maxLength={5000}
          disabled={isSending}
        />
        <Button
          type="submit"
          disabled={!canSend}
          variant="primary"
          className={style.SendBtn}
        >
          Send
        </Button>
      </div>
      {(localError || sendError) && (
        <div className={style.Error}>{localError ?? sendError}</div>
      )}
    </form>
  );
}
